<template>
  <div class="map-wrapper">
    <div ref="map" class="map" role="application" tabindex="-1"></div>
  </div>
</template>

<script>
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';

import colors from '@/util/colors';

import { getEmptyFeature, getEmptyFeatureSet} from '@/util/api'

mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;
const mapboxLanguage = new MapboxLanguage();
// These fonts are served through Mapbox; they are not typical web fonts.
const fontStack = ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'];
const SPATIAL_DATA_KEY = 'devappsspatial';

export default {
  name: 'map-view',
  data: function() {
    return {
      lang: this.$i18n.currentLanguage(),
      lng: -75.786,
      lat: 45.242,
      zoom: 9,
      map: {},
    };
  },
  methods: {
    validCoordinates: function(longitude,latitude){
      if( latitude < 44.90736985128308 || latitude > 45.580879804226164)
        return false;
      if( longitude < -76.42147473076695 || longitude > -75.16493985530872)
        return false;

      return true;
    },
    getGeoBound: function(features, padding) {
      let s = 90
      let e = -180
      let w = 0
      let n = 0

      for(let i = 0; i < features.length; i++){
        let coordinates = features[i].geometry.coordinates;
        if( coordinates[0] < w )
          w = coordinates[0] - padding
        if( coordinates[0] > e )
          e = coordinates[0] + padding
        if( coordinates[1] < s )
          s = coordinates[1] - padding
        if( coordinates[1] > n )
          n = coordinates[1] + padding
      }

      return [w, s, e, n]
    },
  },
  computed: {
    geoData: function() {
      let padding = 0
      let allFeatures = getEmptyFeatureSet();
      let addressIds = []
      let coordinates = []
      let devApps = []
      let isDevApp = false
      let isSearchResult = false
      //let lang = this.$i18n.currentLanguage()
      padding = 0.05
      //console.log('MapView', 'Checking for DevApp')
      if( this.$store.state.currentDevapp ) {
        //console.log('MapView', 'DevApp found', Date.now())
        devApps.push(this.$store.state.currentDevapp)
        //padding = 0.002
        isDevApp = true
      } else if(this.$store.state.searchResults) {
        //console.log('MapView', 'SearchResults found', Date.now())
        devApps = this.$store.state.searchResults.devApps
        //padding = 0.008
        isSearchResult = true
      } else if (this.$store.state.allFeatures) {
        //devApps = this.$store.state.allFeatures
        allFeatures.features = this.$store.state.allFeatures
      }

      if((isDevApp || isSearchResult ) && devApps.length > 0) {
        //console.log('Creating featuresSet')
        for (let devAppId in devApps) {
          const devapp = devApps[devAppId];
          //console.log('MapView', 'Found DevApp', devapp)
          if (devapp && devapp.devAppAddresses ) {
            for (let addressId in devapp.devAppAddresses) {
              let feature = getEmptyFeature();
              let address = devapp.devAppAddresses[addressId];
              let address_id = address.addressReferenceId + '-' + address.parcelPinNumber;

              if(!this.validCoordinates(address.addressLongitude,address.addressLatitude))
                continue;

              if( addressIds.includes(address_id) )
                continue;

              addressIds.push(address_id);
              feature.Id = address_id;

              feature.properties.devAppId = devapp.devAppId;
              feature.properties.applicationNumber = devapp.applicationNumber;
              feature.properties.applicationStatus = devapp.applicationStatus;
              feature.properties.applicationType = devapp.applicationType;
              feature.properties.devAppAddress = address;
              feature.geometry.coordinates.push(address.addressLongitude);
              feature.geometry.coordinates.push(address.addressLatitude);
              coordinates.push(feature.geometry.coordinates)
              allFeatures.features.push(feature);
            }
          }
        }
      }
      //console.log(allFeatures)

      if((isDevApp || isSearchResult) && allFeatures.features.length > 0) {
        let bounds = this.getGeoBound(allFeatures.features, padding)
        this.map.fitBounds(bounds)
      } else {
        this.map.fitBounds([-76.42147473076695,44.90736985128308,-75.16493985530872,45.580879804226164])
      }
      return allFeatures;
    },
    checkDevapps: function() {
      return this.$store.state.currentDevapp
    },
  },
  watch: {
    checkDevapps: function(devApp) {
      if(devApp === null)
        this.map.resize()
    },/*
    geoData: function(newGeoData) {
      // TODO: Can we simplify this check?
      if (this.map && this.map.getSource(SPATIAL_DATA_KEY)) {
        this.map.getSource(SPATIAL_DATA_KEY).setData(newGeoData);
      }
      this.map.resize();
    }*/
  },
  created: function() {
    this.$router.options.navHooks.push(to => {
      // Force the map to resize after a route change (it only listens
      // on window resize events), since different routes may have different
      // sizes for the map
      if (to.meta.showMap) {
        const map = this.map;
        setTimeout(() => {
          map.resize();
        }, 100);
      }
    });
  },
  updated: function() {
    this.map.resize();
  },
  mounted: function() {
    const vm = this;
    const mapContainer = vm.$refs.map;

    vm.map = new mapboxgl.Map({
      container: mapContainer,
      style: {
        'version': 8,
        'sprite': 'mapbox://sprites/mapbox/light-v9',
        'glyphs': 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
        'sources': {
          'raster-tiles-city': {
            'type': 'raster',
            'tiles': ['https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}'],
            'tileSize': 256,
            'attribution': ''
          },
          'raster-tiles-esri': {
            'type': 'raster',
            'tiles': ['https://maps.ottawa.ca/arcgis/rest/services/Basemap_Ottawa_Grey_123/MapServer/tile/{z}/{y}/{x}'],
            'tileSize': 256,
            'attribution': '',
            'bounds': [-76.42147473076695,44.90736985128308,-75.16493985530872,45.580879804226164]
          },
          'raster-wms': {
            'type': 'raster',
            'tiles': ['https://maps.ottawa.ca/arcgis/services/Wards/MapServer/WMSServer?bbox={bbox-epsg-3857}&format=image/png&service=WMS&version=1.3.0&request=GetMap&crs=EPSG:3857&transparent=true&width=2048&height=2048&layers=0&styles=default'],
            'tileSize': 2048
          }
        },
        'layers': [{
          'id': 'simple-tiles-city',
          'type': 'raster',
          'source': 'raster-tiles-city',
          'tileSize': 256,
          'minzoom': 0,
          'maxzoom': 18
        },{
          'id': 'simple-tiles-esri',
          'type': 'raster',
          'source': 'raster-tiles-esri',
          'tileSize': 256,
          'minzoom': 0,
          'maxzoom': 18,
          'bounds': [-76.42147473076695,44.90736985128308,-75.16493985530872,45.580879804226164]
        },{
          'id': 'wms',
          'type': 'raster',
          'source': 'raster-wms'
        }]
      },
      'center': [-75.786,45.242],
      'zoom': 9.1
    });
    vm.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }));
    //vm.map.addControl(new mapboxgl.GeolocateControl());

    vm.map.on('load', () => {
      // Resize the map to fill its container
      // TODO: improve load time
      vm.map.resize();

      vm.map.setStyle(
        mapboxLanguage.setLanguage(
          vm.map.getStyle(),
          vm.$i18n.currentLanguage()
        )
      );

      vm.map.addSource(SPATIAL_DATA_KEY, {
        type: 'geojson',
        // Point to GeoJSON data.
        data: vm.geoData,
        cluster: true,
        clusterMaxZoom: 14, // Max zoom to cluster points on
        clusterRadius: 50 // Radius of each cluster when clustering points (defaults to 50)
      });
      vm.map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: SPATIAL_DATA_KEY,
        filter: ['has', 'point_count'],
        paint: {
          // Use step expressions (https://www.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
          'circle-color': [
            'step',
            ['get', 'point_count'],
            colors.ORANGE,
            100,
            colors.ORANGE
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            100,
            30,
            750,
            40
          ]
        }
      });
      vm.map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: SPATIAL_DATA_KEY,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count}',
          'text-font': fontStack,
          'text-size': 14
        },
        paint: {
          'text-color': colors.WHITE
        }
      });

      vm.map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: SPATIAL_DATA_KEY,
        filter: ['!has', 'point_count'],
        paint: {
          'circle-color': colors.ORANGE,
          'circle-radius': 6,
          'circle-stroke-width': 1,
          'circle-stroke-color': colors.BLACK
        }
      });

      // Create a popup, but don't add it to the map yet.
      let popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
      });

      vm.map.on('mouseenter', 'unclustered-point', function(e) {
        // Change the cursor style as a UI indicator.
        vm.map.getCanvas().style.cursor = 'pointer';

        let coordinates = e.features[0].geometry.coordinates.slice();

        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        let lang = vm.$i18n.currentLanguage()
        let app = e.features[0].properties;
        let popupMsg = ''
        let address = ''
        let applicationType = ''
        let applicationStatus = ''
        let applicationNumber = ''
        //console.log('Feature Popup:', app)
        //console.log('Type:', typeof app)

        if(app.devAppAddress && app.devAppAddress.roadName) {
          address = app.devAppAddress.addressNumber + ' ' + app.devAppAddress.roadName
        } else {
          let objAddress = JSON.parse(app.devAppAddress)
          address = objAddress.addressNumber + ' ' + objAddress.roadName
        }

        if(app.applicationType && app.applicationType['en']) {
          applicationType = app.applicationType[lang]
        } else {
          let objAppType = JSON.parse(app.applicationType)
          applicationType = objAppType[lang]
        }

        if(app.applicationStatus && app.applicationStatus['en']) {
          applicationStatus = app.applicationStatus[lang]
        } else {
          let objAppStatus = JSON.parse(app.applicationStatus)
          applicationStatus = objAppStatus[lang]
        }

        applicationNumber = app.applicationNumber

        if( lang === 'en') {
          popupMsg = '<strong>Address: </strong>' + address
              + '<br/><strong>Application Type: </strong>' + applicationType
              + '<br /><strong>Application Status: </strong>' + applicationStatus
              + '<br /><strong>Applicaiton #: </strong>' + applicationNumber;
        } else {
          popupMsg =  '<br/><strong>Address: </strong>' + address
              + '<br/><strong>Type d\'application: </strong>' + applicationType
              + '<br /><strong>Statut d\'application: </strong>' + applicationStatus
              + '<br /><strong>Application #: </strong>' + applicationNumber;
        }

        // Populate the popup and set its coordinates
        // based on the feature found.
        // TODO: add more info to popup, plus link to details page
        popup
          .setLngLat(coordinates)
          .setHTML(popupMsg)
          .addTo(vm.map)
      })

      vm.map.on('mouseleave', 'unclustered-point', function() {
        vm.map.getCanvas().style.cursor = ''
        popup.remove()
      })

      vm.map.on('click', 'clusters', function (e) {

        var features = vm.map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        var clusterId = features[0].properties.cluster_id;
        var point_count = features[0].properties.point_count;
        var clusterSource = vm.map.getSource(SPATIAL_DATA_KEY);

        //console.log('features', features);
        //console.log('clusterId', clusterId);
        //console.log('point_count', point_count);
        //console.log('clusterSource', clusterSource);
        // Get all points under a cluster
        clusterSource.getClusterLeaves(clusterId, point_count, 0, function(err, aFeatures){
          let bounds = '0,0,0,0'
          if(aFeatures.length > 0) {
            //console.log('aFeatures Length', aFeatures.length)
            let s = 90
            let e = -180
            let w = 0
            let n = 0

            for(let i = 0; i < aFeatures.length; i++){
              let coordinates = aFeatures[i].geometry.coordinates;
              if( coordinates[0] < w )
                w = coordinates[0]
              if( coordinates[0] > e )
                e = coordinates[0]
              if( coordinates[1] < s )
                s = coordinates[1]
              if( coordinates[1] > n )
                n = coordinates[1]
            }
            bounds =  w + ',' + s + ',' + e + ',' + n;
            console.log('bounds:', bounds)
          }

          vm.$store.commit('bounds', bounds)
          vm.$emit('update-map')
        })
      });

      vm.map.on('click', 'unclustered-point', function (e) {
        var features = vm.map.queryRenderedFeatures(e.point, { layers: ['unclustered-point'] });
        vm.$store.commit('currentDevapp', null);
        //console.log('DevAppId', features[0].properties.devAppId)

        vm.$router.push({
          name: 'DevappDetails',
          params: {
            //id: features[0].properties.devAppId
            id: features[0].properties.applicationNumber
          }
        })
      });
    })

    this.map.resize();
  },
}
</script>

<style scoped lang="scss">
@import '@/scss/app.scss';

$mobile-margin: 0.75em;

.map-wrapper {
  padding: 5px;
  width: 100%;
  height: 100%;
  position: relative;
}

.map {
  height: 100%;
  width: calc(100% - #{$mobile-margin} - #{$mobile-margin});
  margin: 0 $mobile-margin 0 $mobile-margin;
  overflow: visible;

  .mapboxgl-canvas {
    height: 100%;
    width: 100%;
  }
}

</style>
